// ============================================================================
// meshes.js — low-poly humanoid geometry builders for the instanced crowd.
//
// A warrior is ~7 boxes (<120 tris). Every vertex carries extra attributes so
// the crowd's vertex shader can animate limbs with ZERO per-instance CPU cost:
//   aLimb  (float)  0 core · 1 leftArm · 2 rightArm(+weapon) · 3 leftLeg · 4 rightLeg
//   aPivot (vec3)   the joint to rotate that limb around
//   aSeam  (float)  ember-seam glow mask (0..1)
//   color  (vec3)   baked base colour, multiplied by per-instance faction tint
// Everything merges into ONE non-indexed BufferGeometry per unit type.
// ============================================================================

import * as THREE from 'three';
import {
  UNIT_FOOTSOLDIER, UNIT_ARCHER, UNIT_CAPTAIN, UNIT_WARLORD,
} from '../core/constants.js';

const _m = new THREE.Matrix4();
const _c = new THREE.Color();

// generated textures (public/art) for Benjen's hand-authored rig
const _texLoader = new THREE.TextureLoader();
function loadTex(url) {
  const t = _texLoader.load(url);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

class HumanoidBuilder {
  constructor() {
    this.pos = []; this.norm = []; this.col = [];
    this.limb = []; this.pivot = []; this.seam = []; this.uv = [];
  }
  box(w, h, d, x, y, z, limbId, piv, colorHex, seamVal) {
    const g = new THREE.BoxGeometry(w, h, d).toNonIndexed();
    _m.makeTranslation(x, y, z);
    g.applyMatrix4(_m);
    const p = g.attributes.position.array;
    const n = g.attributes.normal.array;
    const u = g.attributes.uv.array;   // per-face 0..1 UVs → armour texture tiles per box face
    _c.set(colorHex);
    const vCount = p.length / 3;
    for (let i = 0; i < vCount; i++) {
      this.pos.push(p[i * 3], p[i * 3 + 1], p[i * 3 + 2]);
      this.norm.push(n[i * 3], n[i * 3 + 1], n[i * 3 + 2]);
      this.col.push(_c.r, _c.g, _c.b);
      this.limb.push(limbId);
      this.pivot.push(piv[0], piv[1], piv[2]);
      this.seam.push(seamVal);
      this.uv.push(u[i * 2], u[i * 2 + 1]);
    }
    g.dispose();
  }
  build() {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(this.pos, 3));
    geo.setAttribute('normal', new THREE.Float32BufferAttribute(this.norm, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(this.col, 3));
    geo.setAttribute('aLimb', new THREE.Float32BufferAttribute(this.limb, 1));
    geo.setAttribute('aPivot', new THREE.Float32BufferAttribute(this.pivot, 3));
    geo.setAttribute('aSeam', new THREE.Float32BufferAttribute(this.seam, 1));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(this.uv, 2));
    geo.computeBoundingSphere();
    return geo;
  }
}

// Pivot points (roughly), tuned to a ~1.8m tall figure standing on y=0.
const SH_L = [0.30, 1.36, 0];    // left shoulder
const SH_R = [-0.30, 1.36, 0];   // right shoulder
const HIP_L = [0.15, 0.86, 0];
const HIP_R = [-0.15, 0.86, 0];
const CORE = [0, 0, 0];

function warriorGeometry(cfg) {
  const b = new HumanoidBuilder();
  const body = cfg.body, limbC = cfg.limb, weap = cfg.weapon, seam = cfg.seam;
  // legs
  b.box(0.22, 0.86, 0.24, 0.15, 0.43, 0, 3, HIP_L, limbC, 0);
  b.box(0.22, 0.86, 0.24, -0.15, 0.43, 0, 4, HIP_R, limbC, 0);
  // torso (obsidian plate with ember seam down the front)
  b.box(0.62, 0.62, 0.34, 0, 1.16, 0, 0, CORE, body, seam);
  // pauldron-ish shoulders
  b.box(0.24, 0.22, 0.30, 0.34, 1.34, 0, 1, SH_L, body, seam * 0.7);
  b.box(0.24, 0.22, 0.30, -0.34, 1.34, 0, 2, SH_R, body, seam * 0.7);
  // arms
  b.box(0.17, 0.62, 0.18, 0.36, 1.02, 0.02, 1, SH_L, limbC, seam * 0.4);
  b.box(0.17, 0.62, 0.18, -0.36, 1.02, 0.02, 2, SH_R, limbC, seam * 0.4);
  // head + helmet (skullcap, brow band, nose guard)
  b.box(0.30, 0.30, 0.30, 0, 1.66, 0, 0, CORE, body, 0);
  b.box(0.33, 0.12, 0.33, 0, 1.79, 0, 0, CORE, cfg.helmColor || limbC, seam * 0.5);   // brow band
  b.box(0.09, 0.17, 0.10, 0, 1.63, 0.17, 0, CORE, cfg.helmColor || limbC, 0);          // nose guard
  if (cfg.crest) {
    b.box(0.08, 0.26, 0.34, 0, 1.94, 0, 0, CORE, cfg.crestColor, 1.0);
  }
  // tabard over the torso + waist belt (dresses the figure)
  b.box(0.42, 0.52, 0.05, 0, 0.86, 0.19, 0, CORE, cfg.tabardColor || limbC, seam * 0.6);
  b.box(0.66, 0.11, 0.38, 0, 0.87, 0, 0, CORE, cfg.beltColor || 0x33302b, 0);
  // weapon on the right arm (aLimb 2 → swings with the sword arm)
  if (cfg.bow) {
    b.box(0.06, 1.0, 0.06, -0.44, 1.05, 0.30, 2, SH_R, weap, 0.2);            // bow stave
    b.box(0.15, 0.5, 0.15, 0.24, 1.25, -0.20, 0, CORE, 0x3a2a1a, 0);          // quiver on the back
    b.box(0.03, 0.5, 0.03, 0.24, 1.52, -0.20, 0, CORE, 0xcfc0a0, 0.3);        // arrow fletching
  } else {
    // straight blade held down-forward; officers get a broad greatsword
    const L = cfg.blade || 1.05;
    const bw = cfg.wide ? 0.20 : 0.11;
    b.box(bw, L, 0.05, -0.46, 1.02 - L * 0.2, 0.42, 2, SH_R, weap, cfg.bladeSeam || 0); // blade
    b.box(0.28, 0.10, 0.16, -0.46, 1.32, 0.30, 2, SH_R, cfg.guardColor || weap, 0);      // crossguard
    b.box(0.09, 0.18, 0.11, -0.46, 1.45, 0.30, 2, SH_R, cfg.guardColor || weap, 0.2);    // grip + pommel
  }
  if (cfg.shield) {
    // kite shield strapped to the left arm (aLimb 1 → moves with that arm)
    b.box(0.05, 0.54, 0.42, 0.52, 1.0, 0.14, 1, SH_L, cfg.shieldColor || body, seam * 0.4);
    b.box(0.09, 0.13, 0.13, 0.55, 1.02, 0.14, 1, SH_L, cfg.shieldBoss || 0x8a6a2a, 0.7); // boss
  }
  if (cfg.cape) {
    b.box(0.56, 0.9, 0.06, 0, 1.05, -0.22, 0, CORE, cfg.capeColor, 0.3);
  }
  // NOTE: no baked scale — per-unit scale is applied in the instance matrix
  // (crowd.js) from UNIT_STATS so it isn't double-counted.
  return b.build();
}

// One geometry per unit archetype. Faction tint comes from per-instance colour,
// so baked colours are near-neutral greys that read under any tint.
export function buildUnitGeometry(type) {
  switch (type) {
    case UNIT_FOOTSOLDIER:
      return warriorGeometry({
        body: 0x726f78, limb: 0x5c5960, weapon: 0x3d3d47, seam: 1.0, blade: 1.05,
        shield: true, shieldColor: 0x66626a, shieldBoss: 0x8a6a2a,
      });
    case UNIT_ARCHER:
      return warriorGeometry({
        body: 0x6c6a74, limb: 0x565460, weapon: 0x4a3a2a, seam: 0.7, bow: true,
      });
    case UNIT_CAPTAIN:
      return warriorGeometry({
        body: 0x807c86, limb: 0x605c66, weapon: 0x33333d, seam: 1.0, blade: 1.35, wide: true,
        bladeSeam: 0.8, crest: true, crestColor: 0xff6a1a, guardColor: 0x2a2a30, tabardColor: 0x4a2418,
      });
    case UNIT_WARLORD:
      return warriorGeometry({
        body: 0x8a868f, limb: 0x66626c, weapon: 0x2a2a34, seam: 1.0, blade: 1.7, wide: true,
        bladeSeam: 1.0, crest: true, crestColor: 0xffb257, guardColor: 0x1c1c22,
        cape: true, capeColor: 0x201a1c, tabardColor: 0x2a2028,
      });
    default:
      return warriorGeometry({ body: 0x707070, limb: 0x505050, weapon: 0x303030, seam: 0.5 });
  }
}

// ---- Benjen Daguth: a hand-authored hero rig — articulated joint groups
// (hip→knee, shoulder→elbow, pivot at the top of each limb per the house
// pattern) under layered black-and-silver plate, a two-part tattered cape and
// Masamune. The player renderer poses the joints on the CPU. The blade stays a
// single 1.7-long box centred at local y 0.75 — the sword trail samples its
// ±0.85 endpoints, so that contract must not change. ----
export function buildBenjen() {
  const group = new THREE.Group();
  const benjenTex = loadTex('art/benjen.png');
  const plate = new THREE.MeshLambertMaterial({ color: 0xb4b0bc, map: benjenTex, emissive: 0x160b06, emissiveIntensity: 0.35 });
  const dark = new THREE.MeshLambertMaterial({ color: 0x22242c, emissive: 0x0c0d12, emissiveIntensity: 0.3 });
  const silver = new THREE.MeshLambertMaterial({ color: 0xaab2c0, emissive: 0x1a1d22, emissiveIntensity: 0.35 });
  const cloth = new THREE.MeshLambertMaterial({ color: 0x14100e });
  const mk = (w, h, d, mat) => new THREE.Mesh(new THREE.BoxGeometry(w, h, d), mat);

  // ---- legs: hip group → thigh → knee group → shin plate + boot ----
  const mkLeg = (side) => {
    const hip = new THREE.Group(); hip.position.set(side * 0.17, 0.94, 0);
    const thigh = mk(0.24, 0.5, 0.26, plate); thigh.position.set(0, -0.25, 0); hip.add(thigh);
    const tasset = mk(0.28, 0.24, 0.3, dark); tasset.position.set(0, -0.08, 0); hip.add(tasset);   // fauld plate
    const knee = new THREE.Group(); knee.position.set(0, -0.48, 0); hip.add(knee);
    const shin = mk(0.2, 0.46, 0.22, plate); shin.position.set(0, -0.22, 0); knee.add(shin);
    const shinPlate = mk(0.16, 0.34, 0.06, silver); shinPlate.position.set(0, -0.2, 0.12); knee.add(shinPlate);
    const boot = mk(0.22, 0.12, 0.34, dark); boot.position.set(0, -0.42, 0.05); knee.add(boot);
    return { hip, knee };
  };
  const L = mkLeg(1), R = mkLeg(-1);

  // ---- torso + dressing ----
  const torso = mk(0.66, 0.72, 0.38, plate); torso.position.set(0, 1.28, 0);
  const chest = mk(0.52, 0.34, 0.42, silver); chest.position.set(0, 1.4, 0.02);
  const plack = mk(0.3, 0.2, 0.44, dark); plack.position.set(0, 1.16, 0.02);          // abdominal plate
  const belt = mk(0.7, 0.12, 0.42, cloth); belt.position.set(0, 0.98, 0);
  const buckle = mk(0.13, 0.13, 0.05, silver); buckle.position.set(0, 0.98, 0.22);
  const collar = mk(0.44, 0.14, 0.4, dark); collar.position.set(0, 1.66, -0.02);      // high collar

  // ---- head: crested black helm ----
  const headG = new THREE.Group(); headG.position.set(0, 1.86, 0);
  const skull = mk(0.32, 0.34, 0.32, plate); headG.add(skull);
  const visor = mk(0.34, 0.08, 0.05, dark); visor.position.set(0, 0.02, 0.16); headG.add(visor);
  const cheekL = mk(0.05, 0.18, 0.22, silver); cheekL.position.set(0.17, -0.04, 0.04); headG.add(cheekL);
  const cheekR = mk(0.05, 0.18, 0.22, silver); cheekR.position.set(-0.17, -0.04, 0.04); headG.add(cheekR);
  const crest = mk(0.06, 0.16, 0.44, silver); crest.position.set(0, 0.24, -0.04); headG.add(crest);
  const crestTail = mk(0.05, 0.3, 0.3, dark); crestTail.position.set(0, 0.3, -0.26); crestTail.rotation.x = 0.5; headG.add(crestTail);

  // ---- layered pauldrons ----
  const paulL1 = mk(0.34, 0.26, 0.4, silver); paulL1.position.set(0.46, 1.6, 0);
  const paulL2 = mk(0.28, 0.16, 0.34, dark); paulL2.position.set(0.5, 1.44, 0);
  const paulR1 = mk(0.34, 0.26, 0.4, silver); paulR1.position.set(-0.46, 1.6, 0);
  const paulR2 = mk(0.28, 0.16, 0.34, dark); paulR2.position.set(-0.5, 1.44, 0);

  // ---- cape: pivoted at the shoulders, tapered tail ----
  const cape = new THREE.Group(); cape.position.set(0, 1.7, -0.24);
  const capeMain = mk(0.64, 1.15, 0.05, cloth); capeMain.position.set(0, -0.58, 0); cape.add(capeMain);
  const capeTail = mk(0.5, 0.3, 0.05, cloth); capeTail.position.set(0, -1.28, 0.01); cape.add(capeTail);

  // ---- arms: shoulder group → upper → elbow group → forearm + gauntlet ----
  const mkArm = (side) => {
    const sh = new THREE.Group(); sh.position.set(side * 0.44, 1.54, 0);
    sh.rotation.order = 'YXZ';   // lift (x) THEN yaw (y) — makes horizontal sweeps real fans
    const upper = mk(0.18, 0.4, 0.2, plate); upper.position.set(0, -0.19, 0); sh.add(upper);
    const elb = new THREE.Group(); elb.position.set(0, -0.4, 0); sh.add(elb);
    const fore = mk(0.17, 0.36, 0.19, plate); fore.position.set(0, -0.17, 0); elb.add(fore);
    const gauntlet = mk(0.2, 0.16, 0.22, silver); gauntlet.position.set(0, -0.32, 0); elb.add(gauntlet);
    return { sh, elb };
  };
  const AL = mkArm(1), AR = mkArm(-1);

  // ---- Masamune — the black blade, seated IN the right fist (the grip sits on
  // the pivot origin so the sword rotates around the hand, not ahead of it).
  // (blade = ONE 1.7-tall box centred at y 0.75: the trail samples y ±0.85.)
  const swordPivot = new THREE.Group();
  swordPivot.position.set(0, -0.34, 0.06);   // at the gauntlet
  const blade = mk(0.11, 1.7, 0.16, new THREE.MeshLambertMaterial({ color: 0x3c3c46, map: loadTex('art/weapon.png'), emissive: 0x140a06, emissiveIntensity: 0.5 }));
  blade.position.set(0, 0.75, 0.02);
  const fuller = mk(0.125, 1.3, 0.04, dark); fuller.position.set(0, 0.62, 0.02);      // dark stripe on both flats
  const edge = mk(0.04, 1.7, 0.05, new THREE.MeshBasicMaterial({ color: 0xff6a1a }));
  edge.position.set(0.055, 0.75, 0.02);
  const guard = mk(0.4, 0.09, 0.24, silver); guard.position.set(0, 0.06, 0.02);       // tsuba just above the fist
  const grip = mk(0.09, 0.3, 0.11, cloth); grip.position.set(0, -0.1, 0.02);
  const pommel = mk(0.12, 0.1, 0.14, silver); pommel.position.set(0, -0.28, 0.02);
  swordPivot.add(blade, fuller, edge, guard, grip, pommel);
  AR.elb.add(swordPivot);

  group.add(L.hip, R.hip, torso, chest, plack, belt, buckle, collar, headG,
    paulL1, paulL2, paulR1, paulR2, cape, AL.sh, AR.sh);
  return {
    group,
    parts: {
      legL: L.hip, legR: R.hip, kneeL: L.knee, kneeR: R.knee,
      shL: AL.sh, shR: AR.sh, elbL: AL.elb, elbR: AR.elb,
      torso, head: headG, cape, swordPivot, blade, edge,
    },
  };
}
